import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { services } from '@config/services';
import type { OnboardingStatusResponse, OnboardingStepStatus } from './types';

/**
 * Mirrors /portal/src/app/services/portalServices.js (the dashboard
 * subset). Endpoint:
 * GET /portal/v1/onboarding-status/{merchantId}
 * The Dashboard fetches this when `omMerchantOnboardingEnabled` is on AND
 * a merchant context is resolved. Skip via the hook's `skip` option when
 * there is no merchant (e.g. super-admin with no merchant filter).
 */
export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['OnboardingStatus'],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    getOnboardingStatus: builder.query<OnboardingStatusResponse, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.portal}/v1/onboarding-status/${encodeURIComponent(merchantId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, { merchantId }) => [{ type: 'OnboardingStatus', id: merchantId }],
    }),
    /**
     * Mark an onboarding step complete/incomplete. Direct port of
     * portalServices.updateOnboardingStepStatus:
     * PATCH /portal/v1/onboarding-status/{merchantId}/steps/{stepKey}
     * The legacy portal fired this when the user finished a tracked task
     * (e.g. created a catalog → `review_catalog`, invited a peer user →
     * `invite_peer_user`). Invalidating the status tag re-fetches the
     * dashboard so the matching step card flips to complete.
     */
    updateOnboardingStepStatus: builder.mutation<
      OnboardingStatusResponse,
      { merchantId: string; stepKey: string; status: OnboardingStepStatus }
    >({
      query: ({ merchantId, stepKey, status }) => ({
        url: `${services.portal}/v1/onboarding-status/${encodeURIComponent(merchantId)}/steps/${encodeURIComponent(stepKey)}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { merchantId }) => [{ type: 'OnboardingStatus', id: merchantId }],
    }),
  }),
});

export const { useGetOnboardingStatusQuery, useUpdateOnboardingStepStatusMutation } = onboardingApi;

/**
 * `allMandatoryStepsComplete` — direct port of * `allMandatoryOnboardingStepsCompleteSelector`. Steps without `optional(): true`
 * are mandatory; the dashboard hides the OnboardingSteps card when they're
 * all `status === 'complete'`.
 */
export function allMandatoryStepsComplete(data: OnboardingStatusResponse | undefined): boolean {
  const steps = data?.onboardingStatus?.steps ?? [];
  const mandatory = steps.filter((s) => !s.optional);
  return mandatory.length > 0 && mandatory.every((s) => s.status === 'complete');
}
